import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage, Language } from "@/contexts/LanguageContext";

// Tracks the current authenticated user id to avoid resetting state on tab-focus SIGNED_IN events
let _currentUserIdRef: string | null = null;

export type AppRole = "owner" | "admin" | "manager" | "member" | "revops" | "viewer";

export interface UserProfile {
  id: string;
  auth_user_id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  preferred_language: string;
  job_title: string | null;
  created_at: string;
  updated_at: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string | null;
  default_language: string;
  logo_url: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserRoleInfo {
  id: string;
  user_id: string;
  organization_id: string;
  role: AppRole;
  permission_profile_id: string | null;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null | undefined;
  organization: Organization | null | undefined;
  organizations: Organization[];
  userRole: AppRole | null | undefined;
  permissionProfileId: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isManager: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithMagicLink: (email: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  refreshProfile: () => Promise<void>;
  createOrganization: (name: string) => Promise<{ error: Error | null; organization?: Organization }>;
  switchOrganization: (orgId: string) => void;
  needsOrgSelection: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null | undefined>(undefined);
  const [organization, setOrganization] = useState<Organization | null | undefined>(undefined);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [userRole, setUserRole] = useState<AppRole | null | undefined>(undefined);
  const [permissionProfileId, setPermissionProfileId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsOrgSelection, setNeedsOrgSelection] = useState(false);
  const { setLanguage } = useLanguage();

  const hasInitializedLanguage = useRef(false);
  const requestIdRef = useRef(0);

  const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase.from("user_profiles").select("*").eq("auth_user_id", userId).maybeSingle();
      if (error) throw error;
      if (data && data.avatar_url) {
        // Cache-bust avatar URL to ensure latest version is shown
        const separator = data.avatar_url.includes("?") ? "&" : "?";
        (data as any).avatar_url = `${data.avatar_url}${separator}t=${Date.now()}`;
      }
      return data as UserProfile | null;
    } catch (error) {
      console.error("Error fetching profile:", error);
      return null;
    }
  };

  const fetchOrganizationsAndRole = async (
    profileId: string,
  ): Promise<{
    organizations: Organization[];
    activeOrg: Organization | null;
    role: AppRole | null;
    permProfileId: string | null;
    multiOrg: boolean;
  }> => {
    try {
      const { data: roleData, error: roleError } = await supabase
        .from("user_roles")
        .select("*, organizations(*)")
        .eq("user_id", profileId)
        .order("created_at", { ascending: false });

      if (roleError) throw roleError;

      if (roleData && roleData.length > 0) {
        const allOrgs = roleData.map((r) => r.organizations as unknown as Organization).filter(Boolean);

        // Check if user has a preferred org stored
        const selectedOrgId = localStorage.getItem("selectedOrganizationId");
        let activeRole = roleData[0];

        if (selectedOrgId) {
          const found = roleData.find((r) => r.organization_id === selectedOrgId);
          if (found) activeRole = found;
        }

        const org = activeRole.organizations as unknown as Organization;
        return {
          organizations: allOrgs,
          activeOrg: org,
          role: activeRole.role as AppRole,
          permProfileId: (activeRole as any).permission_profile_id || null,
          multiOrg: allOrgs.length > 1,
        };
      }
      return { organizations: [], activeOrg: null, role: null, permProfileId: null, multiOrg: false };
    } catch (error) {
      console.error("Error fetching organizations and role:", error);
      return { organizations: [], activeOrg: null, role: null, permProfileId: null, multiOrg: false };
    }
  };

  const applyLanguageFromProfile = useCallback(
    (preferredLanguage: string | null) => {
      if (preferredLanguage && !hasInitializedLanguage.current) {
        setLanguage(preferredLanguage as Language);
        hasInitializedLanguage.current = true;
      }
    },
    [setLanguage],
  );

  const loadUserContext = useCallback(
    async (authUserId: string, currentRequestId: number): Promise<void> => {
      const profileData = await fetchProfile(authUserId);
      if (requestIdRef.current !== currentRequestId) return;

      let orgs: Organization[] = [];
      let org: Organization | null = null;
      let role: AppRole | null = null;
      let permProfileId: string | null = null;
      let multiOrg = false;

      if (profileData) {
        const orgResult = await fetchOrganizationsAndRole(profileData.id);
        if (requestIdRef.current !== currentRequestId) return;

        orgs = orgResult.organizations;
        org = orgResult.activeOrg;
        role = orgResult.role;
        permProfileId = orgResult.permProfileId;
        multiOrg = orgResult.multiOrg;

        applyLanguageFromProfile(profileData.preferred_language);
      }

      setProfile(profileData);
      setOrganizations(orgs);
      setOrganization(org);
      setUserRole(role);
      setPermissionProfileId(permProfileId);
      setNeedsOrgSelection(multiOrg && !localStorage.getItem("selectedOrganizationId"));
      setIsLoading(false);
    },
    [applyLanguageFromProfile],
  );

  const refreshProfile = async () => {
    if (!user) return;
    const currentRequestId = ++requestIdRef.current;
    await loadUserContext(user.id, currentRequestId);
  };

  const switchOrganization = (orgId: string) => {
    localStorage.setItem("selectedOrganizationId", orgId);
    setNeedsOrgSelection(false);
    if (user) {
      const currentRequestId = ++requestIdRef.current;
      setIsLoading(true);
      loadUserContext(user.id, currentRequestId);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      if (!isMounted) return;
      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (event === "SIGNED_OUT") {
        // Full cleanup
        _currentUserIdRef = null;
        setProfile(null);
        setOrganization(null);
        setOrganizations([]);
        setUserRole(null);
        setPermissionProfileId(null);
        setIsLoading(false);
        hasInitializedLanguage.current = false;
        localStorage.removeItem("selectedOrganizationId");
      } else if (event === "SIGNED_IN" && currentSession?.user) {
        const isSameUser = _currentUserIdRef === currentSession.user.id;
        _currentUserIdRef = currentSession.user.id;

        if (isSameUser) {
          // Tab-focus or token refresh re-emitting SIGNED_IN for the same user.
          // Silently refresh context WITHOUT resetting state or showing loader.
          const currentRequestId = ++requestIdRef.current;
          loadUserContext(currentSession.user.id, currentRequestId);
        } else {
          // Genuinely new sign-in (different user or first login)
          const currentRequestId = ++requestIdRef.current;
          setIsLoading(true);
          setProfile(undefined);
          setOrganization(undefined);
          setUserRole(undefined);
          loadUserContext(currentSession.user.id, currentRequestId);
        }
      }
      // TOKEN_REFRESHED, USER_UPDATED, etc → session/user already updated above, no reset needed
    });

    supabase.auth.getSession().then(async ({ data: { session: initialSession } }) => {
      if (!isMounted) return;
      setSession(initialSession);
      setUser(initialSession?.user ?? null);

      if (initialSession?.user) {
        _currentUserIdRef = initialSession.user.id;
        const currentRequestId = ++requestIdRef.current;
        setIsLoading(true);
        setProfile(undefined);
        setOrganization(undefined);
        setUserRole(undefined);
        await loadUserContext(initialSession.user.id, currentRequestId);
      } else {
        _currentUserIdRef = null;
        setProfile(null);
        setOrganization(null);
        setUserRole(null);
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [loadUserContext]);

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName }, emailRedirectTo: window.location.origin },
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signInWithMagicLink = async (email: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("send-magic-link", {
        body: { email, redirectTo: window.location.origin },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setOrganization(null);
    setOrganizations([]);
    setUserRole(null);
    setPermissionProfileId(null);
    localStorage.removeItem("selectedOrganizationId");
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const createOrganization = async (name: string) => {
    try {
      if (!profile) throw new Error("Profile not found");
      const { data, error } = await supabase.functions.invoke("create-organization", {
        body: { name },
      });
      if (error) throw error;
      // Force the new org as active context before refreshing
      if (data?.organization?.id) {
        localStorage.setItem("selectedOrganizationId", data.organization.id);
      }
      await refreshProfile();
      return { error: null, organization: data.organization };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const roleStr = userRole as string | null;

  const value: AuthContextType = {
    user,
    session,
    profile,
    organization,
    organizations,
    userRole,
    permissionProfileId,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: roleStr === "admin" || roleStr === "owner",
    isManager: ["owner", "admin", "manager"].includes(roleStr || ""),
    signUp,
    signIn,
    signInWithMagicLink,
    signOut,
    resetPassword,
    updatePassword,
    refreshProfile,
    createOrganization,
    switchOrganization,
    needsOrgSelection,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
